import { Box, ClickAwayListener, SelectChangeEvent, Stack } from "@mui/material"
import { Button, DateCalendar, DatePicker, DateTimeCalendar, Dropdown, getCalendarFormat, Typography } from "@cakemail-org/ui-components-v2"
import { TDropdownChange } from "@cakemail-org/ui-components-v2/dist/esm/components/dropdown/types"
import { MouseEvent, ReactNode, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import "./styles.scss"
import moment, { Moment, MomentInputObject, MomentObjectOutput } from "moment"
import { isUnixRange } from "../../utils/date"

export function DateRangeDD({ onChange, value }: { key?: string, onChange?: TDropdownChange, value: string }) {
    const { t, i18n } = useTranslation()
    const [showDatePicker, setShowDatePicker] = useState(false)
    const [startDate, setStartDate] = useState<Moment>(moment())
    const [endDate, setEndDate] = useState(moment())

    useEffect(() => {
        if (showDatePicker) {
            const fullFormat = value.split("-")
            const hasFullFormat = fullFormat.length > 1
            handleStartDate(hasFullFormat ? moment.unix(Number(fullFormat[0])) : moment())
            handleEndDate(hasFullFormat ? moment.unix(Number(fullFormat[1])) : moment())
        }
    }, [showDatePicker])

    function handleStartDate(value: Moment) {
        setStartDate(value)
    }

    function handleEndDate(value: Moment) {
        setEndDate(value)
    }

    function onCancelDate() {
        setShowDatePicker(false)
    }

    function onApplyDate(event: MouseEvent<HTMLButtonElement>) {
        const formatedValue = startDate.unix() + "-" + endDate.unix()
        onChange && onChange(
            {
                target: {
                    name: "dateCalendar",
                    value: formatedValue
                },
                currentTarget: {
                    name: "dateCalendar",
                    value: formatedValue
                }
            } as SelectChangeEvent<string>,
            null
        )
        onCancelDate()
    }

    function handleOnChange(event: SelectChangeEvent<string>, child: ReactNode, previousValue?: string) {
        const { name, value } = event.target
        if (value !== "abs") {
            onChange && onChange(event, child, previousValue)
        }
    }

    const valueIsFullRange = isUnixRange(value)
    const [valStart, valEnd] = value.split("-").map(Number)
    if (isUnixRange(value)) {
        value = "abs"
    }

    return <Box className={`dateRangePicker ${showDatePicker ? "showPicker" : ""}`}>
        <Dropdown
            color="body1"
            value={value}
            defaultValue="7days"
            onOpen={onCancelDate}
            onChange={handleOnChange}
            options={
                [
                    {
                        text: t("general-last-x-hours", { count: 1 }),
                        value: "1h"
                    },
                    {
                        text: t("general-last-x-days", { count: 1 }),
                        value: "1d"
                    },
                    {
                        text: t("general-last-x-days", { count: 7 }),
                        value: "7d"
                    },
                    {
                        text: t("general-last-x-days", { count: 14 }),
                        value: "14d"
                    },
                    {
                        text: t("general-last-x-days", { count: 30 }),
                        value: "30d"
                    },
                    {
                        onClick: () => { setShowDatePicker(true) },
                        text: t("general-date-picker-custom-range"),
                        renderedText: `${getCalendarFormat({ unixDate: valStart })} ${t("general-to-txt")} ${getCalendarFormat({ unixDate: valEnd })}`,
                        desc: valueIsFullRange ? `${getCalendarFormat({ unixDate: valStart })} ${t("general-to-txt")} ${getCalendarFormat({ unixDate: valEnd })}` : undefined,
                        value: "abs"
                    },
                ]}
        />

        {showDatePicker &&
            <ClickAwayListener onClickAway={onCancelDate}>
                <Stack className="dateCalendarContainer">
                    <Stack direction="row" gap={8}>
                        <Stack>
                            <Typography variant="body1L" className="semiBold">{t("Start Date")}</Typography>
                            <DateTimeCalendar
                                showDaysOutsideCurrentMonth
                                defaultValue={startDate}
                                onChange={handleStartDate}
                            />
                        </Stack>
                        <Stack>
                            <Typography variant="body1L" className="semiBold">{t("End Date")}</Typography>
                            <DateTimeCalendar
                                showDaysOutsideCurrentMonth
                                defaultValue={endDate}
                                onChange={handleEndDate}
                                minDate={startDate}
                            />
                        </Stack>
                    </Stack>
                    <Stack className="actions" direction="row" justifyContent="flex-end" gap={2}>
                        <Button onClick={onCancelDate} size="small" variant="outlined" color="body1">{t("general-cancel")}</Button>
                        <Button onClick={onApplyDate} size="small" color="secondary">{t("general-apply")}</Button>
                    </Stack>
                </Stack>
            </ClickAwayListener>
        }

    </Box>
}